/**
 * Proprietários de um imóvel — o REGISTRO de propriedade, com histórico. Cada linha é um vínculo com
 * período: a aquisição (`data_inicio`) e a venda/transferência (`data_fim`); em aberto = dono atual.
 * Ao registrar um novo proprietário vigente, o backend fecha o dono anterior — a menos que se marque
 * CO-PROPRIEDADE (`encerrar_proprietario_atual=false`), quando os donos coexistem (cada um com sua
 * fração ideal). Espelha o painel de donos do veículo, com o acréscimo da fração e da co-propriedade.
 */
import { BadgeCheck, Building, Pencil, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ComboBuscaEntidade, type ConfigBusca, type ItemBusca } from '@/components/crud/ComboBuscaEntidade';
import { DrawerAdicionar } from '@/components/crud/DrawerAdicionar';
import { Button } from '@/components/ui/button';
import { estadoDaLista } from '@/components/ui/estado';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { formatar } from '@/lib/mascaras';

type Dono = {
  id: string;
  owner_tipo: string;
  owner: { tipo: string; id: string; rotulo: string };
  papel: string;
  data_inicio: string | null;
  data_fim: string | null;
};

const PAPEIS = [
  { valor: 'proprietario', rotulo: 'Proprietário' },
  { valor: 'usufrutuario', rotulo: 'Usufrutuário' },
  { valor: 'nu_proprietario', rotulo: 'Nu-proprietário' },
  { valor: 'compromissario_comprador', rotulo: 'Compromissário comprador' },
  { valor: 'herdeiro', rotulo: 'Herdeiro' },
];
const rotuloPapel = (p: string) => PAPEIS.find((x) => x.valor === p)?.rotulo ?? p;

const BUSCA: Record<'pessoa' | 'empresa', ConfigBusca> = {
  pessoa: {
    endpoint: '/cadastro/pessoas',
    rotulo: (p) => String(p.nome ?? ''),
    sub: (p) => (p.cpf ? formatar(String(p.cpf), 'cpf') : null),
    placeholder: 'Buscar pessoa por nome ou CPF…',
    camposRapido: [
      { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true },
      { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
    ],
  },
  empresa: {
    endpoint: '/cadastro/empresas',
    rotulo: (e) => String(e.razao_social ?? e.nome_fantasia ?? ''),
    sub: (e) => (e.cnpj ? formatar(String(e.cnpj), 'cnpj') : null),
    placeholder: 'Buscar empresa por razão social ou CNPJ…',
    camposRapido: [
      { chave: 'razao_social', rotulo: 'Razão social', tipo: 'texto', obrigatorio: true },
      { chave: 'cnpj', rotulo: 'CNPJ', tipo: 'documento', formato: 'cnpj' },
    ],
  },
};

const dataBR = (iso?: string | null) => (iso ? iso.split('-').reverse().join('/') : '');

function periodo(inicio: string | null, fim: string | null): string {
  const i = dataBR(inicio);
  const f = dataBR(fim);
  if (i && f) return `${i} → ${f}`;
  if (i) return `desde ${i}`;
  if (f) return `até ${f}`;
  return '—';
}

const CAMPO =
  'h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]';

export function PainelDonosImovel({ imovelId }: { imovelId: string }) {
  const base = '/cadastro/imoveis/vinculos';
  const url = `/cadastro/imoveis/${imovelId}/vinculos`;
  const [donos, setDonos] = useState<Dono[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [editando, setEditando] = useState<string | null>(null);

  const [tipoOwner, setTipoOwner] = useState<'pessoa' | 'empresa'>('pessoa');
  const [alvo, setAlvo] = useState<ItemBusca | null>(null);
  const [papel, setPapel] = useState('proprietario');
  const [fracao, setFracao] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [coProp, setCoProp] = useState(false); // co-propriedade: não encerra o dono atual
  const [salvando, setSalvando] = useState(false);

  async function carregar() {
    const r = await api.get<Dono[]>(url);
    setCarregando(false);
    if (r.ok) setDonos(r.dados);
  }

  useEffect(() => {
    setCarregando(true);
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  function limparForm() {
    setAlvo(null);
    setPapel('proprietario');
    setFracao('');
    setInicio('');
    setFim('');
    setCoProp(false);
    setTipoOwner('pessoa');
  }

  async function adicionar(): Promise<boolean> {
    if (!alvo || salvando) return false;
    setSalvando(true);
    const r = await api.post(base, {
      imovel_id: imovelId,
      owner_tipo: tipoOwner,
      owner_id: alvo.id,
      papel,
      fracao: fracao ? fracao.replace(',', '.') : null,
      data_inicio: inicio || null,
      data_fim: fim || null,
      encerrar_proprietario_atual: !coProp,
    });
    setSalvando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return false;
    }
    const trocou = papel === 'proprietario' && !fim && !coProp;
    toast.success(
      trocou
        ? 'Novo proprietário registrado — o dono anterior virou histórico.'
        : coProp
          ? `${BUSCA[tipoOwner].rotulo(alvo)} adicionado como co-proprietário.`
          : `${BUSCA[tipoOwner].rotulo(alvo)} vinculado como ${rotuloPapel(papel)}.`,
    );
    limparForm();
    void carregar();
    return true;
  }

  async function remover(d: Dono) {
    const r = await api.del(`${base}/${d.id}`);
    if (!r.ok) return void toast.error(r.erro);
    toast.success('Vínculo removido.');
    void carregar();
  }

  async function salvarEdicao(d: Dono, novoPapel: string, novoInicio: string, novoFim: string) {
    const r = await api.patch(`${base}/${d.id}`, {
      papel: novoPapel,
      data_inicio: novoInicio || null,
      data_fim: novoFim || null,
    });
    if (!r.ok) return void toast.error(r.erro);
    setEditando(null);
    toast.success('Vínculo atualizado.');
    void carregar();
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building className="size-4 text-accent" />
          Proprietários
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          O registro de propriedade do imóvel, com aquisição e transferência. Um proprietário fica
          atual; ao registrar um novo dono, o anterior vira histórico — salvo co-propriedade, quando
          os donos coexistem (cada um com sua fração).
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {estadoDaLista(carregando, donos.length === 0, 'Nenhum proprietário ainda.') ?? (
          <div className="flex flex-col gap-2">
            {donos.map((d) =>
              editando === d.id ? (
                <FormEdicaoDono
                  key={d.id}
                  dono={d}
                  onCancelar={() => setEditando(null)}
                  onSalvar={(p, i, f) => void salvarEdicao(d, p, i, f)}
                />
              ) : (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-[15px] text-text">{d.owner.rotulo}</span>
                    <span className="tipo-corpo-pequeno text-text-tertiary">
                      {d.owner.tipo === 'empresa' ? 'Empresa' : 'Pessoa'} · {periodo(d.data_inicio, d.data_fim)}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {!d.data_fim && (
                      <Chip aceso>
                        <BadgeCheck className="size-3.5" />
                        Atual
                      </Chip>
                    )}
                    <Chip>{rotuloPapel(d.papel)}</Chip>
                    <Button variant="fantasma" size="sm" onClick={() => setEditando(d.id)} aria-label="Editar">
                      <Pencil />
                    </Button>
                    <Button
                      variant="fantasma"
                      size="sm"
                      className="text-danger hover:text-danger"
                      onClick={() => void remover(d)}
                      aria-label="Remover"
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </div>
              ),
            )}
          </div>
        )}

        <DrawerAdicionar
          titulo="Novo proprietário"
          descricao="Busque a pessoa/empresa (ou cadastre na hora), diga o papel, a fração e o período. Sem data de venda, vira o dono atual."
          rotuloAbrir="Adicionar proprietário"
          rotuloSalvar="Vincular"
          podeSalvar={!!alvo}
          salvando={salvando}
          onSalvar={adicionar}
          onFechar={limparForm}
        >
          <div className="flex flex-col gap-2">
            <span className="tipo-rotulo">Quem</span>
            <div className="flex gap-1 rounded-md bg-surface-2 p-1">
              {(['pessoa', 'empresa'] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setTipoOwner(t);
                    setAlvo(null);
                  }}
                  className={`flex-1 rounded-sm px-3 py-1.5 text-[13px] font-medium capitalize transition ${
                    tipoOwner === t ? 'bg-surface text-text shadow-[var(--shadow-sm)]' : 'text-text-tertiary'
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <ComboBuscaEntidade key={tipoOwner} config={BUSCA[tipoOwner]} onSelecionar={setAlvo} />
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Papel</span>
              <select value={papel} onChange={(e) => setPapel(e.target.value)} className={CAMPO}>
                {PAPEIS.map((p) => (
                  <option key={p.valor} value={p.valor}>
                    {p.rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Fração ideal (0–1)</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="ex.: 0,5"
                value={fracao}
                onChange={(e) => setFracao(e.target.value)}
                className={CAMPO}
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Aquisição / início</span>
              <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className={CAMPO} />
            </label>
            <label className="flex flex-1 flex-col gap-2">
              <span className="tipo-rotulo">Transferência / fim (vazio = atual)</span>
              <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className={CAMPO} />
            </label>
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={coProp} onChange={(e) => setCoProp(e.target.checked)} />
            <span className="tipo-corpo-pequeno text-text-secondary">
              Co-proprietário — <strong className="text-text">não encerra</strong> os donos atuais (coexistem)
            </span>
          </label>
          {papel === 'proprietario' && !fim && !coProp && (
            <p className="tipo-corpo-pequeno text-text-tertiary">
              Sem data de transferência, este vira o <strong className="text-text">proprietário atual</strong> — o
              dono vigente anterior é encerrado.
            </p>
          )}
        </DrawerAdicionar>
      </CardContent>
    </Card>
  );
}

function FormEdicaoDono({
  dono,
  onSalvar,
  onCancelar,
}: {
  dono: Dono;
  onSalvar: (papel: string, inicio: string, fim: string) => void;
  onCancelar: () => void;
}) {
  const [papel, setPapel] = useState(dono.papel);
  const [inicio, setInicio] = useState(dono.data_inicio ?? '');
  const [fim, setFim] = useState(dono.data_fim ?? '');
  return (
    <div className="flex flex-col gap-3 rounded-md border border-border-strong bg-surface-2 px-3 py-3">
      <span className="tipo-rotulo text-text-secondary">{dono.owner.rotulo}</span>
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Papel</span>
          <select value={papel} onChange={(e) => setPapel(e.target.value)} className={CAMPO}>
            {PAPEIS.map((p) => (
              <option key={p.valor} value={p.valor}>
                {p.rotulo}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Aquisição</span>
          <input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} className={CAMPO} />
        </label>
        <label className="flex flex-col gap-2">
          <span className="tipo-rotulo">Transferência</span>
          <input type="date" value={fim} onChange={(e) => setFim(e.target.value)} className={CAMPO} />
        </label>
        <Button type="button" variant="secundario" onClick={() => onSalvar(papel, inicio, fim)}>
          Salvar
        </Button>
        <Button type="button" variant="fantasma" onClick={onCancelar} aria-label="Cancelar">
          <X />
        </Button>
      </div>
    </div>
  );
}
